import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Check, X, Loader2, UserCog, Lock, ChevronRight, ChevronDown, Users, Building2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { aaiApi } from '../../../services/aaiApi';
import { PROVIDER_CATEGORIES } from '../../../components/onboarding/providerCatalog';
import type { AaiDepartment, RosterMember, AaiHome } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel, PageHeader, Banner, Bar, BandPill, StatCard } from '../dashboards/ui';

// "Departments" (design §7.2) — maturity by department as bands (a group under
// the min size is suppressed, never shown), with the real management tools
// (rename / set lead / add) underneath. Maturity bands come from the champion
// home lens; the roster/CRUD comes from the departments endpoints.
// Org structure is the owner's domain: everyone with the page sees the list,
// but the management controls only render for manage_departments holders
// (OWNER) — the backend enforces the same gate.
const DepartmentsAdminPage: React.FC = () => {
  const [depts, setDepts] = useState<AaiDepartment[]>([]);
  const [members, setMembers] = useState<RosterMember[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [maturity, setMaturity] = useState<{ department: string; band: string | null; onboarded: number; pct: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLead, setNewLead] = useState('');
  const [newParent, setNewParent] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null); // department id whose members are shown
  const [drawerId, setDrawerId] = useState<string | null>(null); // department id whose full breakdown drawer is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, h, me] = await Promise.all([
        aaiApi.listDepartments(),
        aaiApi.getMembers().catch(() => ({ data: { members: [] } } as any)),
        aaiApi.getHome().catch(() => null),
        aaiApi.getContext().catch(() => null),
      ]);
      setDepts(d.data.departments);
      setMembers(m.data.members || []);
      setCanManage(!!me?.data?.capabilities?.manage_departments);
      const home = h?.data as AaiHome | undefined;
      setMaturity(home && home.role === 'champion' ? home.departments : []);
      setError(null);
    } catch { setError('Could not load departments.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newName.trim() || !newLead) return;
    setBusy(true); setNote(null);
    try { await aaiApi.createDepartment({ name: newName.trim(), lead_email: newLead, ...(newParent ? { parent_id: newParent } : {}) }); setNewName(''); setNewLead(''); setNewParent(''); await load(); }
    catch { setNote('Could not create — a department needs a name and a head (or the name is a duplicate).'); }
    finally { setBusy(false); }
  };
  const rename = async (id: string) => {
    if (!editName.trim()) return;
    setBusy(true); setNote(null);
    try { await aaiApi.updateDepartment(id, { name: editName.trim() }); setEditId(null); await load(); }
    catch { setNote('Rename failed (duplicate name?).'); }
    finally { setBusy(false); }
  };
  const setLead = async (id: string, email: string) => {
    if (!email) { setNote('A department must keep a head — pick a replacement rather than clearing it.'); return; }
    setBusy(true);
    try { await aaiApi.updateDepartment(id, { lead_email: email }); await load(); }
    finally { setBusy(false); }
  };
  const remove = async (id: string) => {
    setBusy(true); setNote(null);
    try { await aaiApi.deleteDepartment(id); await load(); }
    catch (e: any) { setNote(e?.response?.status === 409 ? 'Reassign its members before deleting.' : 'Delete failed.'); }
    finally { setBusy(false); }
  };
  const setParent = async (id: string, parentId: string) => {
    setBusy(true); setNote(null);
    try { await aaiApi.updateDepartment(id, { parent_id: parentId }); await load(); }
    catch { setNote('Could not set the parent (a department cannot sit under itself or its own child).'); }
    finally { setBusy(false); }
  };
  const moveMember = async (email: string, deptId: string) => {
    setBusy(true); setNote(null);
    try { await aaiApi.setMemberDepartment(email, deptId); await load(); }
    catch { setNote('Could not change that member’s department.'); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading departments…" />;
  if (error) return <ErrorNote message={error} />;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Departments"
        subtitle="Every department in the workspace — expand one to see its members. Maturity bands (below) suppress any group under the min size to protect individuals."
      />

      {/* Summary — the design's three headline department stats, all derived */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard value={depts.length} label="Departments enrolled" />
        <StatCard value={depts.reduce((n, d) => n + (d.members || 0), 0)} label="People across departments" />
        {maturity.length > 0 ? (
          <StatCard value={maturity.filter((m) => (m.band || '').toLowerCase() === 'mature').length} label="At Mature band" />
        ) : (
          <StatCard value={depts.reduce((n, d) => n + (d.onboarded || 0), 0)} label="People onboarded" />
        )}
      </div>

      {/* ROL-3: HR / IT intake — named product steps, not consulting calls. */}
      {canManage && <OrgIntakeCard />}

      {/* Maturity bands (read) — the design's primary Departments view */}
      {maturity.length > 0 && (
        <Card className="mb-4">
          <MonoLabel className="mb-3.5">Department maturity</MonoLabel>
          {maturity.map((d) => (
            <div key={d.department} className="flex items-center gap-3 py-2.5 border-t border-brand-100 first:border-0">
              <div className="w-32 font-semibold text-[13px] truncate">{d.department}</div>
              <div className="flex-1"><Bar pct={d.pct} color="brand" track="bg-white" height="h-1.5" /></div>
              <span className="text-[11.5px] text-gray-400 w-20 text-right">{d.onboarded} onboarded</span>
              <div className="min-w-[92px] text-right"><BandPill band={d.band} /></div>
            </div>
          ))}
        </Card>
      )}

      <Banner tone="blue" icon={<Lock size={16} className="text-brand-500 shrink-0" />}>
        Any department below the minimum group size is suppressed, not shown — you see that it's hidden, never the underlying people.
      </Banner>

      {/* Management (real CRUD) — owner only; everyone else reads. */}
      {!canManage && (
        <p className="mb-4 text-[11.5px] text-gray-400 flex items-center gap-1.5">
          <Lock size={13} /> Only the workspace owner can add departments or change who heads them.
        </p>
      )}
      {canManage && (
      <Card className="mb-4">
        <MonoLabel className="mb-2.5">Add a department</MonoLabel>
        <div className="flex gap-2 flex-wrap">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Department name"
            className="flex-1 min-w-[10rem] rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
            onKeyDown={(e) => { if (e.key === 'Enter') create(); }} />
          <select value={newLead} onChange={(e) => setNewLead(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 max-w-[14rem]">
            <option value="">Choose a head (required)…</option>
            {members.map((m) => <option key={m.email} value={m.email}>{m.name || m.email}</option>)}
          </select>
          <select value={newParent} onChange={(e) => setNewParent(e.target.value)}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200 max-w-[14rem]">
            <option value="">Top-level (no parent)</option>
            {depts.map((d) => <option key={d.id} value={d.id}>Under {d.name}</option>)}
          </select>
          <Button variant="brand" onClick={create} disabled={busy || !newName.trim() || !newLead}><Plus size={15} className="mr-1.5" />Add</Button>
        </div>
        <p className="mt-2 text-[11.5px] text-gray-400">Every department must have a head — requests route to them before the AI champion.</p>
      </Card>
      )}

      {note && <div className="mb-3"><ErrorNote message={note} /></div>}

      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[auto_1.6fr_0.7fr_1.4fr_auto] px-5 py-3 border-b border-gray-100 text-[10.5px] font-sans uppercase tracking-wider text-gray-400">
          <span /><span>Department</span><span>People</span><span>Lead</span><span className="text-right">{canManage ? 'Actions' : ''}</span>
        </div>
        {depts.map((d) => {
          const deptMembers = members.filter((m) => (m.department || '') === d.name);
          const open = expanded === d.id;
          return (
          <div key={d.id} className="border-b border-gray-50 last:border-0">
            <div className="grid grid-cols-[auto_1.6fr_0.7fr_1.4fr_auto] items-center px-5 py-3">
              <button onClick={() => setExpanded(open ? null : d.id)} title={open ? 'Hide members' : 'Show members'}
                className="text-gray-400 hover:text-gray-700 pr-1.5">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
              <div className="min-w-0">
                {editId === d.id ? (
                  <div className="flex items-center gap-1.5">
                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') rename(d.id); }}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-[13px] w-40 focus:outline-none focus:ring-2 focus:ring-brand-200" />
                    <button disabled={busy} onClick={() => rename(d.id)} className="text-emerald-600 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={16} />}</button>
                    <button onClick={() => setEditId(null)} className="text-gray-400"><X size={16} /></button>
                  </div>
                ) : canManage ? (
                  <button onClick={() => { setEditId(d.id); setEditName(d.name); }} className="text-[14px] font-medium text-gray-900 hover:text-brand-600 text-left truncate" title="Rename">{d.name}</button>
                ) : (
                  <span className="text-[14px] font-medium text-gray-900 truncate">{d.name}</span>
                )}
                {/* HR's org hierarchy: where this department sits. Owners can re-seat it. */}
                {canManage ? (
                  <select value={d.parent_id || ''} disabled={busy} onChange={(e) => setParent(d.id, e.target.value)}
                    className="mt-0.5 block text-[11px] text-gray-500 rounded border border-transparent hover:border-gray-200 bg-transparent focus:outline-none max-w-[12rem]">
                    <option value="">Top-level</option>
                    {depts.filter((o) => o.id !== d.id).map((o) => <option key={o.id} value={o.id}>Under {o.name}</option>)}
                  </select>
                ) : d.parent_id ? (
                  <span className="block text-[11px] text-gray-400">Under {depts.find((o) => o.id === d.parent_id)?.name || '—'}</span>
                ) : null}
              </div>
              <div className="text-[13px] text-gray-600">{d.members}<span className="text-gray-400"> · {d.onboarded} onb.</span></div>
              <div className="min-w-0">
                {canManage ? (
                  <select value={d.lead_email || ''} disabled={busy} onChange={(e) => setLead(d.id, e.target.value)}
                    className="w-full max-w-[12rem] text-[12.5px] rounded-lg border border-gray-200 px-2 py-1 bg-transparent focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50">
                    <option value="" disabled>— pick a head (required) —</option>
                    {members.map((m) => <option key={m.email} value={m.email}>{m.name || m.email}</option>)}
                    {d.lead_email && !members.some((m) => m.email === d.lead_email) && <option value={d.lead_email}>{d.lead_name || d.lead_email}</option>}
                  </select>
                ) : (
                  <span className="text-[12.5px] text-gray-600 truncate">{d.lead_name || d.lead_email || '—'}</span>
                )}
              </div>
              <div className="flex items-center justify-end gap-2.5">
                {/* Drill-in — opens the full department breakdown drawer (design §7.2 dept detail) */}
                <button onClick={() => setDrawerId(d.id)} title="Open department breakdown"
                  className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-gray-500 hover:border-brand-300 hover:text-brand-600 whitespace-nowrap">
                  Breakdown <ChevronRight size={12} />
                </button>
                {canManage && (
                  <button disabled={busy} onClick={() => remove(d.id)} title={d.members > 0 ? 'Has members — reassign first' : 'Delete'}
                    className="inline-flex items-center text-gray-300 hover:text-red-500 disabled:opacity-50"><Trash2 size={15} /></button>
                )}
              </div>
            </div>
            {open && (
              <div className="bg-gray-50/70 px-5 py-2.5 border-t border-gray-100">
                <MonoLabel className="mb-2 flex items-center gap-1.5"><Users size={12} /> Members ({deptMembers.length})</MonoLabel>
                {deptMembers.length === 0 ? (
                  <p className="text-[12.5px] text-gray-400 py-1">No members in this department yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {deptMembers.map((m) => (
                      <li key={m.email} className="flex items-center gap-3 py-1.5 text-[13px]">
                        <span className="font-medium text-gray-800 truncate">{m.name || m.email}</span>
                        <span className="text-gray-400 truncate hidden sm:inline">{m.email}</span>
                        <span className="ml-auto flex items-center gap-2.5 shrink-0">
                          {m.workspace_role && <span className="text-[11px] font-sans text-gray-400">{m.workspace_role}</span>}
                          <span className={`text-[11.5px] ${m.onboarded ? 'text-emerald-600' : 'text-gray-400'}`}>{m.onboarded ? 'Onboarded' : 'Not yet'}</span>
                          {/* Owner override — move this member to another department. */}
                          {canManage && (
                            <select value={d.id} disabled={busy}
                              onChange={(e) => { if (e.target.value !== d.id) moveMember(m.email, e.target.value); }}
                              title="Change department"
                              className="text-[11.5px] rounded-md border border-gray-200 bg-white px-1.5 py-1 max-w-[9rem] focus:outline-none focus:ring-2 focus:ring-brand-200 disabled:opacity-50">
                              {depts.map((dep) => <option key={dep.id} value={dep.id}>{dep.name}</option>)}
                            </select>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          );
        })}
        {depts.length === 0 && <p className="px-5 py-6 text-[13px] text-gray-400 text-center">No departments yet. Add one above, or they're created automatically as you enroll people.</p>}
      </div>

      <p className="mt-3 text-[11.5px] text-gray-400 flex items-center gap-1.5"><UserCog size={13} /> Departments are also created automatically when you enroll people into a new department.</p>

      {/* Full breakdown drawer — real band/pct/head/people/onboarded + this dept's members */}
      {drawerId && (() => {
        const d = depts.find((x) => x.id === drawerId);
        if (!d) return null;
        const deptMembers = members.filter((m) => (m.department || '') === d.name);
        const mat = maturity.find((m) => m.department === d.name) || null;
        return <DeptDrawer dept={d} members={deptMembers} maturity={mat} onClose={() => setDrawerId(null)} />;
      })()}
    </div>
  );
};

// Initials for a member avatar — first letters of up to two name parts.
const initials = (s: string): string =>
  s.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '?';

// Slide-in department breakdown (design §7.2 "dept" drawer). Everything shown is
// real: maturity band/pct come from the champion home lens (omitted entirely when
// the workspace isn't in that lens or the group is suppressed); head, people and
// onboarded counts come from the department record; the member list is the same
// roster the page already loaded. The design's per-dept Educate/Empower/Equip
// bars and per-dept use-cases are NOT in the real data, so they're left out.
const DeptDrawer: React.FC<{
  dept: AaiDepartment;
  members: RosterMember[];
  maturity: { band: string | null; pct: string } | null;
  onClose: () => void;
}> = ({ dept, members, maturity, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const head = dept.lead_name || dept.lead_email || null;
  const hasMaturity = !!maturity && (!!maturity.band || !!maturity.pct);

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <aside onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full max-w-[440px] bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 h-[58px] border-b border-gray-100 shrink-0">
          <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-500 grid place-items-center shrink-0"><Building2 size={17} /></span>
          <div className="min-w-0 flex-1">
            <MonoLabel>Department</MonoLabel>
            <div className="font-semibold text-[14px] text-gray-900 truncate">{dept.name}</div>
          </div>
          <button onClick={onClose} title="Close" className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 hover:text-gray-700 grid place-items-center shrink-0"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Headline: maturity % + band + head (band/pct only when the real lens has it) */}
          {hasMaturity ? (
            <div className="flex items-center gap-4">
              <div>
                <div className="text-[26px] font-bold tracking-tight text-gray-900 leading-none">{maturity!.pct || '—'}</div>
                <div className="mt-1.5"><BandPill band={maturity!.band} /></div>
              </div>
              <div className="pl-4 border-l border-gray-100">
                <div className="text-[13px] font-semibold text-gray-900 truncate">{head || 'No head set'}</div>
                <div className="text-[12px] text-gray-400">Department head · {dept.members} people</div>
              </div>
            </div>
          ) : (
            <div>
              <div className="text-[13px] font-semibold text-gray-900 truncate">{head || 'No head set'}</div>
              <div className="text-[12px] text-gray-400">Department head · {dept.members} people</div>
            </div>
          )}

          {hasMaturity && maturity!.pct && (
            <div>
              <MonoLabel className="mb-2">Maturity</MonoLabel>
              <Bar pct={maturity!.pct} color="brand" track="bg-gray-100" height="h-2" />
            </div>
          )}

          {/* People / onboarded — real department counts */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <div className="text-[20px] font-bold text-gray-900">{dept.members}</div>
              <div className="text-[11.5px] text-gray-400 mt-0.5">People</div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-3">
              <div className="text-[20px] font-bold text-gray-900">{dept.onboarded}</div>
              <div className="text-[11.5px] text-gray-400 mt-0.5">Onboarded</div>
            </div>
          </div>

          {/* Members — the same roster the page already loaded */}
          <div>
            <MonoLabel className="mb-2 flex items-center gap-1.5"><Users size={12} /> Members ({members.length})</MonoLabel>
            {members.length === 0 ? (
              <p className="text-[12.5px] text-gray-400 py-1">No members in this department yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {members.map((m) => (
                  <li key={m.email} className="flex items-center gap-3 py-2 text-[13px]">
                    <span className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 grid place-items-center text-[11px] font-bold shrink-0">{initials(m.name || m.email)}</span>
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-800 truncate">{m.name || m.email}</span>
                      <span className="block text-[11.5px] text-gray-400 truncate">{m.email}</span>
                    </span>
                    <span className="ml-auto flex items-center gap-2.5 shrink-0">
                      {m.workspace_role && <span className="text-[11px] font-sans text-gray-400">{m.workspace_role}</span>}
                      <span className={`text-[11.5px] ${m.onboarded ? 'text-emerald-600' : 'text-gray-400'}`}>{m.onboarded ? 'Onboarded' : 'Not yet'}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-gray-100 px-5 py-3.5">
          <button onClick={onClose} className="w-full rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-[13px] font-semibold py-2.5">Done</button>
        </div>
      </aside>
    </div>
  );
};

// ROL-3 — the two intake lanes. HR's people list arrives via the Team screen
// (this card only tracks its progress); IT's tool list is recorded here and
// drives dives, blocked capabilities, and What's Allowed. IT gets back
// per-person, per-tool adoption.
type OrgIntake = {
  hr: { training_budget?: string | null; submitted_by?: string; submitted_at?: string };
  it: { tool_stack?: string[]; no_destructive?: boolean; submitted_by?: string; submitted_at?: string };
  give_back: { enrolled: number; with_department: number; with_job_title?: number; usage_by_tool: Array<{ tool: string; people_dived: number; skills_mentioning: number }> };
};

const OrgIntakeCard: React.FC = () => {
  const [intake, setIntake] = useState<OrgIntake | null>(null);
  // Connector-restricted (Usama, 2026-08-25): the stack is picked from the
  // SAME connector catalog the Connectors modal renders — nothing else can
  // enter, and the picker grows automatically as connectors ship.
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [inUse, setInUse] = useState<Array<{ tool: string; users: number }>>([]);
  const [noDestructive, setNoDestructive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await aaiApi.getOrgIntake();
        setIntake(data);
        setSelectedTools(data?.it?.tool_stack || []);
        setNoDestructive(!!data?.it?.no_destructive);
      } catch { setLoadError(true); }
      try { const { data: s } = await aaiApi.getToolSuggestions(); setInUse(s.in_use || []); }
      catch { /* the in-use annotation is a convenience */ }
    })();
  }, []);

  const hasTool = (t: string) => selectedTools.some((x) => x.toLowerCase() === t.toLowerCase());
  const addTool = (t: string) => {
    const v = t.trim();
    if (v && !hasTool(v)) setSelectedTools((cur) => [...cur, v]);
  };
  const removeTool = (t: string) => setSelectedTools((cur) => cur.filter((x) => x !== t));
  // "N in use" annotation: match team-reported names to a catalog provider.
  const usersFor = (p: { slug: string; label: string }) => {
    const hit = inUse.find((u) => {
      const low = u.tool.trim().toLowerCase();
      return low === p.label.toLowerCase() || low.replace(/[\s-]+/g, '_') === p.slug;
    });
    return hit?.users;
  };

  const saveIt = async () => {
    setSaving(true); setNote(null);
    try {
      const { data } = await aaiApi.setItIntake(selectedTools, noDestructive);
      setIntake(data);
      const rejected: string[] = (data as any).rejected || [];
      if (rejected.length) {
        // Connector-restricted: the server refused these — reflect it in the
        // chips instead of leaving them looking saved.
        setSelectedTools((cur) => cur.filter((t) => !rejected.some((r) => r.toLowerCase() === t.toLowerCase())));
        setNote(`Saved. No connector exists for: ${rejected.join(', ')} — removed (the stack only takes connector-supported tools).`);
      } else {
        setNote('Tool stack saved — every participant’s dive queue, blocked capabilities, and What’s Allowed now read from it.');
      }
    } catch { setNote('Could not save the tool stack.'); }
    finally { setSaving(false); }
  };

  if (loadError) {
    return (
      <Card className="mb-4">
        <MonoLabel className="mb-1">Company intake — HR &amp; IT</MonoLabel>
        <p className="text-[12px] text-gray-400">Could not load the intake right now — refresh to try again.</p>
      </Card>
    );
  }
  if (!intake) return null;
  const usage = intake.give_back.usage_by_tool || [];
  return (
    <Card className="mb-4">
      <MonoLabel className="mb-1">Company intake — HR &amp; IT</MonoLabel>
      <p className="text-[12px] text-gray-500 mb-3">
        Every client kickoff needs two things: HR's people list and IT's tool list.
        This card tracks the first and records the second.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-[12.5px] font-semibold text-gray-800 mb-1">People — from HR</p>
          <p className="text-[11.5px] text-gray-500 mb-2">
            HR sends who works here: names, departments, job titles and job descriptions.
            You add them on the <b>Team screen</b> — one by one, or as a CSV (columns:
            email, name, role, department, job_title, job_description). Sub-departments
            are set below (each department's "Under …"). Nothing to fill in here; this
            just shows how far along it is.
          </p>
          <div className="rounded-lg bg-white border border-gray-100 px-3 py-2.5 mb-2">
            <p className="text-[12px] text-gray-700 font-medium mb-0.5">Progress</p>
            <p className="text-[11.5px] text-gray-500">
              {intake.give_back.enrolled} people invited · {intake.give_back.with_department} placed in a department
              {intake.give_back.with_job_title != null && <> · {intake.give_back.with_job_title} with a job title</>}
            </p>
          </div>
          <p className="text-[11px] text-gray-400">
            In return, HR gets the capability map — the org chart with everyone's AI adoption role.
          </p>
        </div>
        <div>
          <p className="text-[12.5px] font-semibold text-gray-800 mb-1">Tools — from IT</p>
          <p className="text-[11.5px] text-gray-500 mb-2">
            IT sends the list of tools the company uses. Pick them below and save — that
            one save sets each person's weekly tool dives, blocks what policy forbids, and
            fills the What's Allowed page. <b>· N</b> = how many teammates already said
            they use it.
          </p>
          {selectedTools.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedTools.map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 px-2.5 py-1 text-[11.5px] font-medium">
                  {t}
                  <button onClick={() => removeTool(t)} className="text-brand-400 hover:text-brand-700" aria-label={`Remove ${t}`}>✕</button>
                </span>
              ))}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto pr-1 mb-2 space-y-1.5">
            {PROVIDER_CATEGORIES.map((cat) => {
              const available = cat.providers.filter((p) => !hasTool(p.label));
              if (!available.length) return null;
              return (
                <div key={cat.id}>
                  <p className="text-[10.5px] font-sans uppercase tracking-wide text-gray-400 mb-1">{cat.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {available.map((p) => {
                      const users = usersFor(p);
                      return (
                        <button key={p.slug} onClick={() => addTool(p.label)}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 hover:border-brand-300 bg-white px-2.5 py-1 text-[11.5px] text-gray-700 whitespace-nowrap">
                          + {p.label}{users != null && <span className="text-gray-400">· {users}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* TD-4: the org-wide ground rule. Blocked capabilities show in every
              dive as "not permitted — company policy", never quietly missing. */}
          <label className="flex items-center gap-2 text-[12px] text-gray-600 mb-2 cursor-pointer">
            <input type="checkbox" checked={noDestructive} onChange={(e) => setNoDestructive(e.target.checked)}
              className="rounded border-gray-300 text-brand-500 focus:ring-brand-300" />
            No destructive actions anywhere (deletes, removals — off for every participant)
          </label>
          <Button size="sm" onClick={saveIt} disabled={saving}>
            {saving ? <Loader2 size={13} className="animate-spin" /> : 'Save tool stack'}
          </Button>
        </div>
      </div>
      {usage.length > 0 && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <p className="text-[11.5px] font-semibold text-gray-600 mb-0.5">What IT gets back — adoption per tool</p>
          <p className="text-[11px] text-gray-400 mb-1.5">Who has dived each tool, and how many marketplace skills use it.</p>
          <div className="flex flex-wrap gap-1.5">
            {usage.map((u) => (
              <span key={u.tool} className="inline-flex items-center gap-1 rounded-full bg-gray-50 border border-gray-200 px-2.5 py-1 text-[11.5px] text-gray-600">
                {u.tool} · {u.people_dived} dived · {u.skills_mentioning} skills
              </span>
            ))}
          </div>
        </div>
      )}
      {note && <p className="text-[11.5px] text-gray-500 mt-2">{note}</p>}
    </Card>
  );
};

export default DepartmentsAdminPage;
